'use client';

import { flattenCategoryTree } from '@/features/categories/categoryTreeFilters';
import IllustrationMotion from '@/features/empty-state/IllustrationMotion';
import {
  setShopFocusIntent,
  SHOP_FOCUS_FILTER_BAR,
} from '@/features/shop/shopFocusIntent';
import { buildShopPath } from '@/features/shop/shopQuery';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import './noResults.scss';

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement ?? '')),
    String(template || ''),
  );
}

function buildBaseSearchParams({
  keyword,
  selectedCategorySlugs,
  sort,
  minRating,
  inStockOnly,
}) {
  return {
    ...(keyword ? { keyword } : {}),
    ...(selectedCategorySlugs.length > 0
      ? { category: selectedCategorySlugs }
      : {}),
    ...(sort && sort !== 'newest' ? { sort } : {}),
    ...(minRating ? { minRating } : {}),
    ...(inStockOnly ? { inStock: 'true' } : {}),
  };
}

export default function ShopNoResultsState({
  keyword = '',
  selectedCategorySlugs = [],
  categoriesTree = [],
  sort = 'newest',
  minRating = '',
  inStockOnly = false,
  messages = {},
  filterMessages = {},
  pathname = '/shop',
}) {
  const containerRef = useRef(null);
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const msg = (key, fallback = '') =>
    typeof messages[key] === 'string' ? messages[key] : fallback;
  const fm = (key, fallback = '') =>
    typeof filterMessages[key] === 'string' ? filterMessages[key] : fallback;
  const flattenedCategories = flattenCategoryTree(categoriesTree || []);
  const selectedCategories = selectedCategorySlugs
    .map((slug) => flattenedCategories.find((entry) => entry.slug === slug))
    .filter(Boolean);
  const sortLabels = {
    newest: fm('sortNewest', 'Newest arrivals'),
    rating: fm('sortRating', 'Top rated'),
    priceAsc: fm('sortPriceAsc', 'Price low to high'),
    priceDesc: fm('sortPriceDesc', 'Price high to low'),
    nameAsc: fm('sortNameAsc', 'Name A to Z'),
  };
  const keywordPrefix = msg('keywordPrefix', fm('keywordLabel', 'Keyword'));
  const categoryPrefix = msg('categoryPrefix', 'Category');
  const sortPrefix = msg('sortPrefix', fm('sortLabel', 'Sort'));
  const ratingPrefix = msg('ratingPrefix', fm('ratingLabel', 'Rating'));
  const stockPrefix = msg('stockPrefix', 'Stock');
  const stockValue = msg('stockValue', fm('inStockOnly', 'In stock only'));
  const reduceMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );
  const baseSearchParams = buildBaseSearchParams({
    keyword,
    selectedCategorySlugs,
    sort,
    minRating,
    inStockOnly,
  });
  const activeFilters = [
    ...(keyword
      ? [
          {
            key: `keyword-${keyword}`,
            kind: 'keyword',
            prefix: keywordPrefix,
            value: keyword,
            label: `${keywordPrefix}: ${keyword}`,
            accentClassName: 'text-warning',
            href: buildShopPath(baseSearchParams, {
              keyword: null,
              page: null,
            }),
          },
        ]
      : []),
    ...selectedCategories.map((category) => ({
      key: `category-${category.slug}`,
      kind: 'category',
      prefix: categoryPrefix,
      value: category.name,
      label: `${categoryPrefix}: ${category.name}`,
      accentClassName: 'text-primary',
      href: buildShopPath(baseSearchParams, {
        category: selectedCategorySlugs.filter(
          (slug) => slug !== category.slug,
        ),
        page: null,
      }),
    })),
    ...(sort !== 'newest'
      ? [
          {
            key: `sort-${sort}`,
            kind: 'sort',
            prefix: sortPrefix,
            value: sortLabels[sort] || sort,
            label: `${sortPrefix}: ${sortLabels[sort] || sort}`,
            accentClassName: 'text-info',
            href: buildShopPath(baseSearchParams, {
              sort: null,
              page: null,
            }),
          },
        ]
      : []),
    ...(minRating
      ? [
          {
            key: `rating-${minRating}`,
            kind: 'rating',
            prefix: ratingPrefix,
            value: fillTemplate(msg('ratingValue', '{{count}}+ stars'), {
              count: minRating,
            }),
            label: `${ratingPrefix}: ${fillTemplate(
              msg('ratingValue', '{{count}}+ stars'),
              { count: minRating },
            )}`,
            accentClassName: 'text-info',
            href: buildShopPath(baseSearchParams, {
              minRating: null,
              page: null,
            }),
          },
        ]
      : []),
    ...(inStockOnly
      ? [
          {
            key: 'in-stock',
            kind: 'stock',
            prefix: stockPrefix,
            value: stockValue,
            variant: 'info',
            label: `${stockPrefix}: ${stockValue}`,
            accentClassName: 'text-info',
            href: buildShopPath(baseSearchParams, {
              inStock: null,
              page: null,
            }),
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (
      reduceMotion ||
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window)
    ) {
      setVisible(true);
      return;
    }

    const element = containerRef.current;

    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.2 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [reduceMotion]);

  const animatedStyle = reduceMotion
    ? undefined
    : {
        transition: 'opacity 320ms ease, transform 320ms ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      };

  const handleReset = () => {
    setShopFocusIntent(SHOP_FOCUS_FILTER_BAR);
    router.push(pathname, { scroll: false });
  };

  const handleRemoveFilter = (href) => {
    setShopFocusIntent(SHOP_FOCUS_FILTER_BAR);
    router.push(href, { scroll: false });
  };

  return (
    <div
      className="no-results"
      style={animatedStyle}
      ref={containerRef}
      aria-live="polite"
    >
      <div className="no-results__left flex-fill d-flex flex-column align-items-center">
        <div className="no-results__img">
          <IllustrationMotion
            src="/images/searchEmpty.jpg"
            alt={msg('illustrationAlt', 'No results found')}
            maxHeight={300}
          />
        </div>

        <div className="left_copy">
          <h2 className="fw-normal">{msg('title', 'No results found')}</h2>
          <p className="mb-3 opacity-75">
            {msg('description', 'Try adjusting your filters or search term.')}
          </p>
        </div>
      </div>

      <div className="no-results__right d-flex flex-column align-items-center justify-content-start flex-fill">
        <Button
          variant="outline-primary"
          size="lg"
          onClick={handleReset}
          className="reset-filters-btn"
          aria-label={msg('resetAction', 'Reset filters')}
        >
          <span aria-hidden="true" className="me-1">
            🧹
          </span>
          {msg('resetAction', 'Reset filters')}
        </Button>

        {activeFilters.length > 0 ? (
          <>
            <p className="mb-0 opacity-75">
              {msg(
                'removeHint',
                'Remove a single filter without losing the rest of your search.',
              )}
            </p>
            <div className="d-flex flex-wrap gap-2 gap-landscape-2 gap-md-3 align-items-center justify-content-center w-100">
              {activeFilters.map((filter) => (
                <OverlayTrigger
                  key={filter.key}
                  placement="top"
                  overlay={
                    <Tooltip>
                      {fillTemplate(
                        msg('removeFilterTooltip', 'Remove {{label}}'),
                        { label: filter.label },
                      )}
                    </Tooltip>
                  }
                >
                  <Button
                    variant="secondary"
                    tabIndex={0}
                    aria-label={fillTemplate(
                      msg('removeFilterTooltip', 'Remove {{label}}'),
                      { label: filter.label },
                    )}
                    onClick={() => handleRemoveFilter(filter.href)}
                    className="badge-fade-in clickable-badge"
                  >
                    <p className="d-flex gap-1 align-items-center mb-0">
                      <FaTimes size={16} color="red" />
                      <span className="opacity-75">{filter.prefix}: </span>
                      <span
                        className={`${filter.accentClassName || ''} fw-medium`}
                      >
                        {filter.value}
                      </span>
                    </p>
                  </Button>
                </OverlayTrigger>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
